import * as d3 from 'd3'
import { BRAND_CONFIG } from '../branding/brandConfig'

const THUMBNAIL = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='320' height='160' viewBox='0 0 320 160'%3e%3crect width='320' height='160' fill='white'/%3e%3cline x1='72' y1='48' x2='156' y2='78' stroke='%239aa4b2' stroke-width='2'/%3e%3cline x1='72' y1='48' x2='116' y2='122' stroke='%239aa4b2' stroke-width='2'/%3e%3cline x1='156' y1='78' x2='244' y2='46' stroke='%239aa4b2' stroke-width='2'/%3e%3cline x1='156' y1='78' x2='226' y2='120' stroke='%239aa4b2' stroke-width='2'/%3e%3ccircle cx='72' cy='48' r='13' fill='%2306c26c'/%3e%3ccircle cx='116' cy='122' r='10' fill='%2395e5c0'/%3e%3ccircle cx='156' cy='78' r='16' fill='%23e8722a'/%3e%3ccircle cx='244' cy='46' r='11' fill='%2306c26c'/%3e%3ccircle cx='226' cy='120' r='12' fill='%2395e5c0'/%3e%3c/svg%3e"
const ICON = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'%3e%3cline x1='14' y1='16' x2='28' y2='28' stroke='%2395e5c0' stroke-width='3'/%3e%3cline x1='28' y1='28' x2='43' y2='15' stroke='%2395e5c0' stroke-width='3'/%3e%3cline x1='28' y1='28' x2='40' y2='42' stroke='%2395e5c0' stroke-width='3'/%3e%3ccircle cx='14' cy='16' r='6' fill='%2306c26c'/%3e%3ccircle cx='28' cy='28' r='7' fill='%23e8722a'/%3e%3ccircle cx='43' cy='15' r='6' fill='%2306c26c'/%3e%3ccircle cx='40' cy='42' r='6' fill='%2306c26c'/%3e%3c/svg%3e"

function mappedColumn(mapping, id) {
  const value = mapping?.[id]?.value
  return Array.isArray(value) ? value[0] || null : value || null
}

function mappedColumns(mapping, id) {
  const value = mapping?.[id]?.value
  return (Array.isArray(value) ? value : [value]).filter(Boolean)
}

function validNodeId(value) {
  return value !== null && value !== undefined && String(value).trim() !== ''
}

// One record is one connection. Identical links are combined only for drawing:
// their count and optional Weight become the line width, while all different
// relationships remain separate connections in the graph.
export function mapForceGraphData(rows = [], mapping = {}) {
  const sourceColumn = mappedColumn(mapping, 'source')
  const targetColumn = mappedColumn(mapping, 'target')
  const relationshipColumn = mappedColumn(mapping, 'relationship')
  const weightColumn = mappedColumn(mapping, 'weight')
  const groupColumn = mappedColumn(mapping, 'group')
  const stepColumns = mappedColumns(mapping, 'steps')
  if (!sourceColumn || !targetColumn) return []

  const links = new Map()
  const addLink = ({ source, target, relationship, group, weight }) => {
    if (!validNodeId(source) || !validNodeId(target)) return
    const sourceId = String(source)
    const targetId = String(target)
    if (sourceId === targetId) return
    const [first, second] = [sourceId, targetId].sort((a, b) => a.localeCompare(b))
    const key = `${first}\u0000${second}\u0000${relationship}`
    const previous = links.get(key)
    links.set(key, previous
      ? { ...previous, weight: previous.weight + weight, count: previous.count + 1 }
      : { source: sourceId, target: targetId, relationship, group, weight, count: 1 })
  }
  rows.forEach((row) => {
    const rawWeight = weightColumn ? Number(row[weightColumn]) : 1
    const weight = Number.isFinite(rawWeight) && rawWeight > 0 ? rawWeight : 1
    const namedRelationship = relationshipColumn && row[relationshipColumn] != null
      ? String(row[relationshipColumn])
      : null
    const namedGroup = groupColumn && row[groupColumn] != null && row[groupColumn] !== ''
      ? String(row[groupColumn])
      : null

    if (stepColumns.length > 1) {
      stepColumns.slice(0, -1).forEach((column, index) => {
        const nextColumn = stepColumns[index + 1]
        const relationship = namedRelationship || `${column} → ${nextColumn}`
        addLink({
          source: row[column], target: row[nextColumn], relationship,
          group: namedGroup || relationship, weight,
        })
      })
      return
    }

    const relationship = namedRelationship || 'Connection'
    addLink({
      source: row[sourceColumn], target: row[targetColumn], relationship,
      group: namedGroup || relationship, weight,
    })
  })
  return Array.from(links.values())
}

function appendGroup(parent, id, name) {
  return parent.append('g').attr('id', id).attr('data-name', name)
}

function getGraphNodes(links) {
  const nodes = new Map()
  links.forEach((link) => {
    ;[link.source, link.target].forEach((id) => {
      if (!nodes.has(id)) nodes.set(id, { id, group: link.group, degree: 0 })
      nodes.get(id).degree += link.count
    })
  })
  return Array.from(nodes.values())
}

function settleGraph(nodes, links, width, height, linkDistance, nodeRadius) {
  const simulation = d3.forceSimulation(nodes)
    .randomSource(d3.randomLcg(0.314159))
    .force('link', d3.forceLink(links).id((node) => node.id).distance(linkDistance).strength(0.7))
    .force('charge', d3.forceManyBody().strength(-Math.max(60, linkDistance * 1.5)))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('x', d3.forceX(width / 2).strength(0.035))
    .force('y', d3.forceY(height / 2).strength(0.035))
    .force('collide', d3.forceCollide().radius((node) => nodeRadius + Math.min(8, Math.sqrt(node.degree))))
    .stop()

  // A bounded synchronous settle produces a stable SVG for preview and export.
  // Fewer ticks for dense graphs keeps the normal chart renderer responsive.
  const ticks = nodes.length > 350 ? 120 : nodes.length > 150 ? 180 : 260
  for (let index = 0; index < ticks; index += 1) simulation.tick()
}

// Force distance is expressed in SVG units, so a compact network can look
// tiny inside a wide artboard. Preserve the settled relationships, then scale
// the finished coordinates into the chart area with room for nodes and labels.
export function fitGraphToViewport(nodes, width, height, padding = 38) {
  if (!nodes.length) return
  const xValues = nodes.map((node) => node.x)
  const yValues = nodes.map((node) => node.y)
  const minX = Math.min(...xValues)
  const maxX = Math.max(...xValues)
  const minY = Math.min(...yValues)
  const maxY = Math.max(...yValues)
  const sourceWidth = Math.max(1, maxX - minX)
  const sourceHeight = Math.max(1, maxY - minY)
  const availableWidth = Math.max(1, width - padding * 2)
  const availableHeight = Math.max(1, height - padding * 2)
  const scale = Math.min(availableWidth / sourceWidth, availableHeight / sourceHeight)
  const fittedWidth = sourceWidth * scale
  const fittedHeight = sourceHeight * scale
  const offsetX = padding + (availableWidth - fittedWidth) / 2
  const offsetY = padding + (availableHeight - fittedHeight) / 2
  nodes.forEach((node) => {
    node.x = offsetX + (node.x - minX) * scale
    node.y = offsetY + (node.y - minY) * scale
  })
}

function labelBounds(candidate) {
  return {
    left: candidate.x,
    right: candidate.x + candidate.width,
    top: candidate.y - candidate.height * 0.78,
    bottom: candidate.y + candidate.height * 0.22,
  }
}

function overlaps(first, second) {
  return first.left < second.right && first.right > second.left
    && first.top < second.bottom && first.bottom > second.top
}

// Labels are more likely to collide than the nodes themselves in dense edge
// lists. Try a few positions around each node, prioritising well-connected
// nodes, and omit only labels with no clear placement.
export function placeForceGraphLabels(nodes, width, height, nodeRadius, fontSize) {
  const placedBounds = []
  const placements = new Map()
  const gap = 4
  const labelHeight = Math.max(10, fontSize * 1.15)
  const orderedNodes = [...nodes].sort((a, b) => b.degree - a.degree || a.id.localeCompare(b.id))

  orderedNodes.forEach((node) => {
    const labelWidth = Math.max(12, String(node.id).length * fontSize * 0.56)
    const candidates = [
      { x: node.x + nodeRadius + gap, y: node.y + fontSize * 0.3, width: labelWidth, height: labelHeight, textAnchor: 'start' },
      { x: node.x - nodeRadius - gap - labelWidth, y: node.y + fontSize * 0.3, width: labelWidth, height: labelHeight, textAnchor: 'start' },
      { x: node.x - labelWidth / 2, y: node.y + nodeRadius + gap + labelHeight, width: labelWidth, height: labelHeight, textAnchor: 'start' },
      { x: node.x - labelWidth / 2, y: node.y - nodeRadius - gap, width: labelWidth, height: labelHeight, textAnchor: 'start' },
    ]
    const placement = candidates.find((candidate) => {
      const bounds = labelBounds(candidate)
      return bounds.left >= 0 && bounds.right <= width && bounds.top >= 0 && bounds.bottom <= height
        && !placedBounds.some((otherBounds) => overlaps(bounds, otherBounds))
    })
    if (!placement) return
    placedBounds.push(labelBounds(placement))
    placements.set(node.id, placement)
  })
  return placements
}

export function renderForceGraph(svgNode, links, visualOptions, mapping) {
  const {
    width,
    height,
    background,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    nodeRadius,
    nodeOpacity,
    nodeColor,
    nodeStroke,
    linkColor,
    linkOpacity,
    linkDistance,
    showLabels,
    labelFontSize,
    colorScale,
  } = visualOptions
  const svg = d3.select(svgNode)
  svg.selectAll('*').remove()
  svg.attr('width', width).attr('height', height).attr('viewBox', `0 0 ${width} ${height}`)
  svg.append('rect').attr('id', 'background').attr('width', width).attr('height', height).attr('fill', background)

  const graphWidth = Math.max(1, width - marginLeft - marginRight)
  const graphHeight = Math.max(1, height - marginTop - marginBottom)
  const root = appendGroup(svg, 'force-graph', 'Force-directed graph')
    .attr('transform', `translate(${marginLeft},${marginTop})`)
  const nodes = getGraphNodes(links)
  if (!nodes.length) {
    root.append('text').attr('x', graphWidth / 2).attr('y', graphHeight / 2).attr('text-anchor', 'middle').attr('fill', '#6b7280').text('Map Source and Target fields to draw connections')
    return
  }

  const graphLinks = links.map((link) => ({ ...link }))
  settleGraph(nodes, graphLinks, graphWidth, graphHeight, linkDistance, nodeRadius)
  fitGraphToViewport(nodes, graphWidth, graphHeight, Math.max(28, nodeRadius + 28))
  const maxWeight = d3.max(graphLinks, (link) => link.weight) || 1
  const widthScale = d3.scaleSqrt().domain([1, maxWeight]).range([1, 5])
  const groupIsMapped = Boolean(mappedColumn(mapping, 'group'))
  const linkLayer = appendGroup(root, 'force-graph-links', 'Links')
  const nodeLayer = appendGroup(root, 'force-graph-nodes', 'Nodes')

  linkLayer.selectAll('line').data(graphLinks).join('line')
    .attr('x1', (link) => link.source.x).attr('y1', (link) => link.source.y)
    .attr('x2', (link) => link.target.x).attr('y2', (link) => link.target.y)
    .attr('stroke', (link) => groupIsMapped ? colorScale(link.group) : linkColor)
    .attr('stroke-width', (link) => widthScale(link.weight))
    .attr('stroke-opacity', linkOpacity)
    .attr('data-name', 'Connection')

  nodeLayer.selectAll('circle').data(nodes).join('circle')
    .attr('cx', (node) => node.x).attr('cy', (node) => node.y)
    .attr('r', (node) => nodeRadius + Math.min(6, Math.sqrt(node.degree)))
    .attr('fill', (node) => groupIsMapped ? colorScale(node.group) : nodeColor)
    .attr('stroke', nodeStroke).attr('stroke-width', 1.25).attr('opacity', nodeOpacity)
    .attr('data-name', 'Node')

  if (showLabels) {
    const placements = placeForceGraphLabels(nodes, graphWidth, graphHeight, nodeRadius, labelFontSize)
    appendGroup(root, 'force-graph-labels', 'Node labels').selectAll('text').data(nodes.filter((node) => placements.has(node.id))).join('text')
      .attr('x', (node) => placements.get(node.id).x).attr('y', (node) => placements.get(node.id).y)
      .attr('text-anchor', (node) => placements.get(node.id).textAnchor)
      .attr('font-size', labelFontSize).attr('fill', '#27303b').attr('data-name', 'Node label')
      .text((node) => node.id)
  }
}

const forceDirectedGraph = {
  metadata: {
    name: 'Force-directed graph',
    id: 'rawgraphs.forcedirectedgraph',
    thumbnail: THUMBNAIL,
    icon: ICON,
    categories: ['networks', 'relationships'],
    description: 'It maps an edge list into a network. Each row is one connection between a Source and a Target.',
    code: '',
    tutorial: '',
  },
  dimensions: [
    { id: 'source', name: 'Source', validTypes: ['number', 'string', 'date'], required: true, operation: 'get' },
    { id: 'target', name: 'Target', validTypes: ['number', 'string', 'date'], required: true, operation: 'get' },
    { id: 'steps', name: 'Steps', validTypes: ['number', 'string', 'date'], required: false, multiple: true, minValues: 2, operation: 'get' },
    { id: 'relationship', name: 'Relationship', validTypes: ['number', 'string', 'date'], required: false, operation: 'get' },
    { id: 'weight', name: 'Weight', validTypes: ['number'], required: false, operation: 'get' },
    { id: 'group', name: 'Node group', validTypes: ['number', 'string', 'date'], required: false, operation: 'get' },
  ],
  mapData: mapForceGraphData,
  render: renderForceGraph,
  visualOptions: {
    marginTop: { type: 'number', label: 'Margin (top)', default: 28, group: 'artboard' },
    marginRight: { type: 'number', label: 'Margin (right)', default: 28, group: 'artboard' },
    marginBottom: { type: 'number', label: 'Margin (bottom)', default: 28, group: 'artboard' },
    marginLeft: { type: 'number', label: 'Margin (left)', default: 28, group: 'artboard' },
    nodeRadius: { type: 'number', label: 'Node radius', default: 6, min: 2, max: 24, group: 'nodes' },
    nodeOpacity: { type: 'number', label: 'Node opacity', default: 0.92, min: 0.1, max: 1, step: 0.05, group: 'nodes' },
    nodeColor: { type: 'color', label: 'Node color', default: BRAND_CONFIG.ui.accentColor, group: 'nodes' },
    nodeStroke: { type: 'color', label: 'Node outline', default: '#ffffff', group: 'nodes' },
    linkDistance: { type: 'number', label: 'Link distance', default: 64, min: 20, max: 220, group: 'layout' },
    linkOpacity: { type: 'number', label: 'Link opacity', default: 0.6, min: 0.1, max: 1, step: 0.05, group: 'links' },
    linkColor: { type: 'color', label: 'Link color', default: '#8b95a5', group: 'links' },
    showLabels: { type: 'boolean', label: 'Show node labels', default: true, group: 'labels' },
    labelFontSize: { type: 'number', label: 'Label size', default: 11, min: 8, max: 24, group: 'labels', disabled: { showLabels: false } },
    colorScale: { type: 'colorScale', label: 'Group colors', dimension: 'group', default: { scaleType: 'ordinal', interpolator: 'schemeCategory10' }, group: 'colors' },
  },
  styles: { labelPrimary: { fontFamily: 'Arial, sans-serif', fontSize: '10px', fill: '#27303b' } },
}

export default forceDirectedGraph
